import { format, isValid, parse } from 'date-fns';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { STATEMENT_STATUSES } from '_constants/statements';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';

import Button from 'components/atomic/Button';
import Icon from 'components/atomic/Icon';
import UploadButton from 'components/atomic/UploadButton';
import Text from 'components/atomic/layouts/Text';

const StyledContainer = styled.div`
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  border-radius: 8px;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: center;
  padding: ${theme.spaces.regular}px;
  border-bottom: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
`;

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spaces.regular}px;
  padding: ${theme.spaces.regular}px;
`;

const StyledRow = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: ${theme.spaces.smallX}px;
`;

const StyledCol = styled.div``;

const StyledStatusContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  height: 24px;
  padding: ${theme.spaces.small2X}px;
  border-radius: 8px;
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  background: ${hexToRGBA(theme.colors.gray.secondary, 0.08)};
`;

const StyledDownloadIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  width: 24px;
  padding: ${theme.spaces.small2X}px;
  border-radius: 8px;
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  background: ${hexToRGBA(theme.colors.gray.secondary, 0.08)};

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      opacity: 0.4;
    `}
`;

const StatementListItem = ({
  id,
  fileName,
  description,
  startDate,
  endDate,
  status,
  type,
  year,
  onDownload,
  onUpload,
  onDelete,
}) => {
  const handleDownload = () => {
    onDownload && onDownload(id, fileName)();
  };

  const handleUpload = (file) => {
    onUpload && onUpload(id)(file);
  };

  const handleDelete = () => {
    onDelete && onDelete(id)();
  };

  const parsedStartDate = parse(startDate, 'yyyy-MM-dd', new Date());
  const parsedEndDate = parse(endDate, 'yyyy-MM-dd', new Date());

  return (
    <StyledContainer>
      <StyledHeader>
        <Text size={theme.fontSizes.small} weight={700}>
          {isValid(parsedEndDate) ? format(parsedEndDate, 'MMMM yyyy') : endDate}
        </Text>
      </StyledHeader>
      <StyledBody>
        <StyledRow>
          <StyledCol>
            <Icon alias={ICON_ALIASES.document} color={theme.colors.gray.secondary} />
          </StyledCol>
          <StyledCol>
            <Text size={theme.fontSizes.regular} weight={700} bottomSpace={theme.spaces.small2X}>
              {description || 'N/A'}
            </Text>
            <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary}>
              Type: {type?.replace(/([A-Z])/g, ' $1').trim() || 'N/A'}
            </Text>
            <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary}>
              Year: {year || 'N/A'}
            </Text>
            <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary}>
              Start date: {isValid(parsedStartDate) ? format(parsedStartDate, 'MM/dd/yyyy') : startDate}
            </Text>
            <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary}>
              End Date: {isValid(parsedEndDate) ? format(parsedEndDate, 'MM/dd/yyyy') : endDate}
            </Text>
          </StyledCol>
        </StyledRow>
        <StyledRow>
          <StyledStatusContainer>
            <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} weight={700}>
              {status?.replace(/([A-Z])/g, ' $1').trim()}
            </Text>
          </StyledStatusContainer>
          <StyledDownloadIconContainer
            disabled={status === STATEMENT_STATUSES.requested}
            title={fileName}
            onClick={handleDownload}
          >
            <Icon alias={ICON_ALIASES.arrowDownCircle} color={theme.colors.gray.secondary} />
          </StyledDownloadIconContainer>
        </StyledRow>
        <UploadButton
          disabled={status === STATEMENT_STATUSES.confirmed}
          color={theme.colors.indigo.primary}
          onChange={handleUpload}
        >
          Click to Upload
        </UploadButton>
        <Button color={theme.colors.red.primary} onClick={handleDelete}>
          Delete
        </Button>
      </StyledBody>
    </StyledContainer>
  );
};

StatementListItem.propTypes = {
  id: PropTypes.string,
  fileName: PropTypes.string,
  description: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  status: PropTypes.string,
  type: PropTypes.string,
  year: PropTypes.string,
  onDownload: PropTypes.func,
  onUpload: PropTypes.func,
  onDelete: PropTypes.func,
};

export default StatementListItem;
